import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { DialogRef } from '../../../../core/services/dialog-ref.service';
import { WhatsAppConsentSource } from '../../../../core/services/contact.service';
import {
  FlowState,
  PhoneSetupMode,
  VerifiedEvent,
  WhatsAppVerifyFlowComponent
} from '../whatsapp-verify-flow/whatsapp-verify-flow.component';

export type { PhoneSetupMode } from '../whatsapp-verify-flow/whatsapp-verify-flow.component';

export interface PhoneSetupDialogData {
  mode?: PhoneSetupMode;
  source?: WhatsAppConsentSource;
  /** Masked verified number, shown in `change` mode. */
  currentMasked?: string | null;
  /** E.164 used only to pre-fill. */
  legacyE164?: string | null;
  countryIso2?: string | null;
  /** Show "Remind me later" (the existing-user prompt). */
  allowSnooze?: boolean;
  pending?: string[];
  backdropDismiss?: boolean;
}

export interface PhoneSetupResult {
  verified?: boolean;
  masked?: string;
  snoozed?: boolean;
}

/** Modal shell around the shared verification flow. */
@Component({
  selector: 'app-phone-setup-dialog',
  standalone: true,
  imports: [CommonModule, WhatsAppVerifyFlowComponent],
  templateUrl: './phone-setup-dialog.component.html',
  styleUrls: ['./phone-setup-dialog.component.scss']
})
export class PhoneSetupDialogComponent implements OnInit, OnDestroy {
  // Assigned by DialogService from the open() payload.
  @Input() mode: PhoneSetupMode = 'setup';
  @Input() source: WhatsAppConsentSource = 'setup_prompt';
  @Input() currentMasked: string | null = null;
  @Input() legacyE164: string | null = null;
  @Input() countryIso2: string | null = null;
  @Input() allowSnooze = false;
  /** Pending setup task ids ('country' | 'whatsapp') to highlight on step 1. */
  @Input() pending: string[] = [];

  @ViewChild('dialogEl', { static: true }) dialogEl!: ElementRef<HTMLElement>;
  @ViewChild(WhatsAppVerifyFlowComponent) flow?: WhatsAppVerifyFlowComponent;

  state: FlowState = { step: 'number', maskedNumber: '' };
  doneMasked = '';

  private previouslyFocused: Element | null = null;
  private closed = false;
  private closeSub?: Subscription;

  constructor(
    private dialogRef: DialogRef,
    private cdr: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    this.previouslyFocused = document.activeElement;
    // Backdrop click closes via DialogService directly; stop touching the view afterwards.
    this.closeSub = this.dialogRef.afterClosed$.subscribe(() => (this.closed = true));
  }

  ngOnDestroy(): void {
    this.closeSub?.unsubscribe();
    if (this.previouslyFocused instanceof HTMLElement) {
      this.previouslyFocused.focus?.();
    }
  }

  get title(): string {
    if (this.state.step === 'done') return 'WhatsApp number verified';
    if (this.state.step === 'code') return 'Enter your code';
    return this.mode === 'change' ? 'Change your WhatsApp number' : 'Add your WhatsApp number';
  }

  onState(state: FlowState): void {
    this.state = state;
    if (!this.closed) this.cdr.detectChanges();
  }

  onVerified(e: VerifiedEvent): void {
    this.doneMasked = e.masked;
    setTimeout(() => this.finish({ verified: true, masked: this.doneMasked }), 1800);
  }

  onDismissed(): void {
    this.finish({ verified: true, masked: this.doneMasked });
  }

  onSnoozed(): void {
    this.finish({ snoozed: true });
  }

  cancel(): void {
    if (this.flow?.requestInFlight) return;
    this.finish({});
  }

  private finish(result: PhoneSetupResult): void {
    if (this.closed) return;
    this.closed = true;
    this.dialogRef.close(result);
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (this.closed) return;
    if (event.key === 'Escape') {
      if (this.flow?.closeDropdown()) {
        event.stopPropagation();
        return;
      }
      // Escape is ignored while a request is in flight.
      if (!this.flow?.requestInFlight) {
        event.preventDefault();
        this.state.step === 'done' ? this.finish({ verified: true, masked: this.doneMasked }) : this.cancel();
      }
      return;
    }
    if (event.key === 'Tab') this.trapFocus(event);
  }

  private trapFocus(event: KeyboardEvent): void {
    const nodes = this.focusable();
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    const active = document.activeElement as HTMLElement | null;
    const inside = !!active && this.host.nativeElement.contains(active);
    if (!inside) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  private focusable(): HTMLElement[] {
    const sel =
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';
    return Array.from(this.dialogEl.nativeElement.querySelectorAll<HTMLElement>(sel)).filter(
      (el) => el.offsetParent !== null
    );
  }
}
